import { useState, useEffect, useRef } from "react";
import { waitForOnboardingFlagSync } from "@/lib/onboardingFlag";
import { useNavigate, useSearchParams, Link, useLocation } from "react-router-dom";
import { Mail, Lock, User, Eye, EyeOff, ArrowLeft, AtSign, Loader2, Check, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { Mascot } from "@/components/Mascot";
import { PasswordStrengthIndicator } from "@/components/PasswordStrengthIndicator";
import { getOAuthRedirectUri, isNativeShell } from "@/lib/oauthRedirect";
import {
  abortPopupOAuth,
  getOAuthPopupProvider,
  getOAuthPopupRedirectUri,
  isOAuthPopup,
  isOAuthPopupCallback,
  notifyOAuthOpener,
  tryPopupOAuth,
} from "@/lib/oauthPopup";
import { SEO } from "@/components/SEO";
import { getStoredReferralCode } from "@/lib/referral";
import { passwordSchema, PASSWORD_REQUIREMENTS_TEXT } from "@/lib/passwordPolicy";
import { friendlyPasswordError } from "@/lib/authErrors";
import { passwordLeakError } from "@/lib/leakedPassword";


const signinSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  // Sign-in stays lenient: legacy accounts created under the old 6-char policy
  // must still be able to log in. The server decides if credentials match.
  password: z.string().min(6, "Password must be at least 6 characters"),
});

const signupSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: passwordSchema,
  firstName: z.string().min(1, "First name is required").max(50, "First name is too long"),
  lastName: z.string().min(1, "Last name is required").max(50, "Last name is too long"),
  username: z.string()
    .min(3, "Username must be at least 3 characters")
    .max(30, "Username must be less than 30 characters")
    .regex(/^[a-zA-Z0-9._]+$/, "Only letters, numbers, dots, and underscores"),
});

type AuthMode = 'signin' | 'signup' | 'forgot-password' | 'reset-password' | 'confirm-email';

export default function Auth() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Legacy redirect: old recovery emails point to /auth?mode=reset-password.
  // Forward them to the dedicated /reset-password route, preserving the hash
  // (the recovery token lives in the URL fragment).
  if (typeof window !== "undefined" && searchParams.get("mode") === "reset-password") {
    const hash = window.location.hash || "";
    navigate(`/reset-password${hash}`, { replace: true });
  }

  const hasResetPasswordMode = searchParams.get("mode") === "reset-password";

  // Detect recovery flow from URL BEFORE Supabase strips the hash.
  // This flag persists across re-renders so SIGNED_IN cannot race past it.
  const isRecoveryFlow = useRef(() => {
    const hash = window.location.hash;
    const search = window.location.search;
    return hasResetPasswordMode || hash.includes('type=recovery') || search.includes('type=recovery');
  });
  const recoveryDetected = useRef(isRecoveryFlow.current());

  const [mode, setMode] = useState<AuthMode>(() => {
    if (recoveryDetected.current) return 'reset-password';
    return searchParams.get("mode") === "signup" ? 'signup' : 'signin';
  });
  // /auth?mode=signup&email= (from the city waitlist card) fills the email in.
  const [email, setEmail] = useState(() => {
    const prefill = searchParams.get("email")?.trim() ?? "";
    return z.string().email().max(255).safeParse(prefill).success ? prefill : "";
  });
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [resending, setResending] = useState(false);
  const { toast } = useToast();
  
  // Where to redirect after login (query param ?redirect= takes precedence over location.state)
  const redirectParam = searchParams.get("redirect");
  const safeRedirect = redirectParam && redirectParam.startsWith("/") && !redirectParam.startsWith("//")
    ? redirectParam
    : null;
  const from = safeRedirect || (location.state as { from?: string })?.from || "/browse";
  const modeRef = useRef(mode);
  modeRef.current = mode;

  // Handle tokens passed back from native deeplink (com.despia.grounded://oauth/auth?access_token=...&refresh_token=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const accessToken = params.get("access_token");
    const refreshToken = params.get("refresh_token");
    if (accessToken && refreshToken) {
      setLoading(true);
      supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
        .then(({ error }) => {
          if (error) {
            toast({ title: "Sign-in failed", description: error.message, variant: "destructive" });
          }
        })
        .finally(() => setLoading(false));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // One move per visit: the already-signed-in check and the sign-in listener
  // share this guard. It is claimed only after the recovery and popup checks
  // pass and a session exists.
  const movedRef = useRef(false);

  const routeSignedIn = async (userId: string, replace: boolean) => {
    if (movedRef.current) return;
    movedRef.current = true;
    const go = (to: string) => (replace ? navigate(to, { replace: true }) : navigate(to));
    // Rule 3d: the device flag (and marker cleanup) must finish first.
    await waitForOnboardingFlagSync(userId).catch(() => {});
    if (safeRedirect) {
      go(safeRedirect);
      return;
    }
    let roles: string[] = [];
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .in('role', ['admin', 'owner']);
      if (!error) roles = (data ?? []).map((r) => r.role);
    } catch {
      roles = [];
    }
    if (roles.includes('admin')) {
      go('/admin');
    } else if (roles.includes('owner')) {
      go('/owner');
    } else if (localStorage.getItem('grounded_onboarding_in_progress')) {
      // Read only after the sync above, which clears it for finished accounts.
      go('/?auth_complete=1');
    } else {
      go(from);
    }
  };
  const routeSignedInRef = useRef(routeSignedIn);
  routeSignedInRef.current = routeSignedIn;

  useEffect(() => {
    const completePopupSignIn = () => {
      notifyOAuthOpener({ type: "oauth-complete" });
      window.close();
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") {
        recoveryDetected.current = true;
        setMode('reset-password');
        return;
      }
      if (session?.user && modeRef.current !== 'reset-password' && !recoveryDetected.current) {
        if (isOAuthPopup()) {
          completePopupSignIn();
          return;
        }
        void routeSignedInRef.current(session.user.id, false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (mode === 'reset-password' || recoveryDetected.current || isOAuthPopup()) return;
    let cancelled = false;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (cancelled || !session?.user) return;
      if (modeRef.current === 'reset-password' || recoveryDetected.current) return;
      void routeSignedInRef.current(session.user.id, true);
    });
    return () => {
      cancelled = true;
    };
  }, [mode]);

  useEffect(() => {
    if (!isOAuthPopup() || isOAuthPopupCallback()) return;

    const provider = getOAuthPopupProvider();
    if (!provider) {
      notifyOAuthOpener({ type: "oauth-error", message: "Missing OAuth provider." });
      window.close();
      return;
    }

    let cancelled = false;

    const startPopupOAuth = async () => {
      setLoading(true);
      try {
        const { error } = await lovable.auth.signInWithOAuth(provider, {
          redirect_uri: getOAuthPopupRedirectUri(),
        });

        if (error) throw error;
      } catch (error: any) {
        if (cancelled) return;
        notifyOAuthOpener({
          type: "oauth-error",
          message: error?.message || "Sign-in failed.",
        });
        window.close();
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    startPopupOAuth();

    return () => {
      cancelled = true;
    };
  }, []);

  // Check username availability with debounce
  useEffect(() => {
    if (mode !== 'signup' || !username || username.length < 3) {
      setUsernameAvailable(null);
      return;
    }
    if (!/^[a-zA-Z0-9._]+$/.test(username)) {
      setUsernameAvailable(false);
      return;
    }
    const timer = setTimeout(async () => {
      setIsCheckingUsername(true);
      const { data } = await supabase
        .from('profiles')
        .select('id')
        .eq('username', username.toLowerCase())
        .maybeSingle();
      setUsernameAvailable(data === null);
      setIsCheckingUsername(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [username, mode]);

  const validateForm = () => {
    try {
      if (mode === 'forgot-password') {
        z.object({ email: z.string().email("Please enter a valid email address") }).parse({ email });
      } else if (mode === 'reset-password') {
        const resetSchema = z.object({
          password: passwordSchema,
          confirmPassword: z.string(),
        }).refine((data) => data.password === data.confirmPassword, {
          message: "Passwords don't match",
          path: ["confirmPassword"],
        });
        resetSchema.parse({ password, confirmPassword });
      } else if (mode === 'signup') {
        signupSchema.parse({ email, password, firstName, lastName, username });
      } else {
        signinSchema.parse({ email, password });
      }
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        err.errors.forEach((e) => {
          if (e.path[0]) newErrors[e.path[0] as string] = e.message;
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleForgotPassword = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      toast({
        title: "Check your email",
        description: "We've sent you a password reset link.",
      });
      setMode('signin');
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    const leakError = await passwordLeakError(password);
    if (leakError) {
      setErrors((prev) => ({ ...prev, password: leakError }));
      setLoading(false);
      return;
    }
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      
      toast({
        title: "Password updated!",
        description: "Your password has been reset successfully.",
      });
      navigate(from);
    } catch (error: any) {
      let message = error.message;
      if (message.includes("Auth session missing")) {
        message = "Your reset link has expired. Please request a new one.";
      }
      message = friendlyPasswordError(error, message);
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!email) return;
    setResending(true);
    try {
      const { error } = await supabase.auth.resend({ type: "signup", email });
      if (error) throw error;
      toast({
        title: "Confirmation resent",
        description: `We sent another link to ${email}.`,
      });
    } catch (error: any) {
      toast({
        title: "Couldn't resend",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setResending(false);
    }
  };

  const startNativeOAuth = async (provider: "google" | "apple") => {
    const redirectUri = `${getOAuthRedirectUri()}/native-callback`;

    // Use managed Lovable OAuth for both providers so the Google consent
    // screen shows "Grounded" / groundedtheapp.com instead of the raw
    // Supabase project URL.
    const { error } = await lovable.auth.signInWithOAuth(provider, {
      redirect_uri: redirectUri,
    });
    if (error) throw error;
  };

  // Detects when a native OAuth sheet (ASWebAuthenticationSession on iOS) is
  // dismissed by the user without completing sign-in. When the document
  // regains visibility and no session arrives within ~1s, clear loading so
  // the buttons unfreeze quickly (vs. waiting for a long timeout).
  const watchNativeCancel = (): (() => void) => {
    let cancelled = false;
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      window.setTimeout(async () => {
        if (cancelled) return;
        const { data } = await supabase.auth.getSession();
        if (cancelled) return;
        if (!data.session) {
          setLoading(false);
        }
      }, 1000);
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
    };
  };

  const handleGoogleSignIn = async () => {
    // Order matters: abort BEFORE setting loading=true. The aborted attempt's
    // finally runs as a microtask with loading still false, so it can't clobber
    // the new flow's loading state.
    abortPopupOAuth();
    setLoading(true);
    let stopWatch: (() => void) | null = null;
    try {
      if (isNativeShell()) {
        stopWatch = watchNativeCancel();
        await startNativeOAuth("google");
        return;
      }

      const popup = await tryPopupOAuth("google");
      if (popup.handled) {
        if (popup.error) throw popup.error;
        return;
      }

      const { error } = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: getOAuthRedirectUri(),
      });
      if (error) throw error;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      stopWatch?.();
      setLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    // Order matters: abort BEFORE setting loading=true. See handleGoogleSignIn.
    abortPopupOAuth();
    setLoading(true);
    let stopWatch: (() => void) | null = null;
    try {
      if (isNativeShell()) {
        stopWatch = watchNativeCancel();
        await startNativeOAuth("apple");
        return;
      }

      const { error } = await lovable.auth.signInWithOAuth("apple", {
        redirect_uri: getOAuthRedirectUri(),
      });
      if (error) throw error;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      stopWatch?.();
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (mode === 'forgot-password') {
      handleForgotPassword();
      return;
    }
    
    if (mode === 'reset-password') {
      handlePasswordReset();
      return;
    }
    
    if (!validateForm()) return;

    // Block signup if username is taken or still checking
    if (mode === 'signup' && (usernameAvailable === false || isCheckingUsername)) {
      setErrors(prev => ({ ...prev, username: 'This username is already taken' }));
      return;
    }

    setLoading(true);

    if (mode === 'signup') {
      const leakError = await passwordLeakError(password);
      if (leakError) {
        setErrors((prev) => ({ ...prev, password: leakError }));
        setLoading(false);
        return;
      }
    }

    try {
      if (mode === 'signup') {
        const referralCode = getStoredReferralCode();
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/`,
            data: {
              first_name: firstName,
              last_name: lastName,
              username: username.toLowerCase(),
              ...(referralCode ? { referral_code: referralCode } : {}),
            },
          },
        });
        if (error) throw error;
        localStorage.setItem('grounded_just_signed_up', '1');

        // When email confirmation is required, signUp returns a user but no
        // session — the account isn't usable until they click the link. Show a
        // dedicated "check your inbox" screen instead of a misleading success
        // toast. When confirmation is off (auto-confirm), a session comes back
        // and the auth listener navigates onward, so keep the welcome toast.
        if (!data.session) {
          setMode('confirm-email');
        } else {
          toast({
            title: "Welcome to Grounded!",
            description: "Your account has been created successfully.",
          });
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        toast({
          title: "Welcome back!",
          description: "You've signed in successfully.",
        });
      }
    } catch (error: any) {
      let message = error.message;
      if (message.includes("User already registered")) {
        message = "This email is already registered. Try signing in instead.";
      } else if (message.includes("Invalid login credentials")) {
        message = "Invalid email or password. Please try again.";
      } else {
        message = friendlyPasswordError(error, message);
      }
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-dvh gradient-warm-hero flex flex-col">
      <SEO
        title="Sign In or Sign Up | Grounded"
        description="Sign in to Grounded to save favorite cafes, check in, and contribute community ratings for work-friendly spots."
        path="/auth"
        noindex
      />
      {/* Header */}
      <header className="p-4">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-4 w-4" />
          <span>Back to home</span>
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 pb-8 -mt-4">
        <div className="w-full max-w-md md:max-w-lg">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center gap-3">
              <Mascot 
                mood={mode === 'signup' ? "excited" : mode === 'forgot-password' || mode === 'reset-password' ? "thinking" : "waving"} 
                size="md" 
              />
              <span className="font-display text-2xl font-bold text-logo">grounded</span>
            </div>
          </div>

          {/* Auth Card */}
          <div className="card-craft rounded-2xl p-8">
            {mode === 'confirm-email' ? (
              <div className="text-center space-y-5">
                <h1 className="font-display text-2xl font-bold text-foreground">
                  Check your inbox
                </h1>
                <p className="text-muted-foreground">
                  We sent a confirmation link to{" "}
                  <span className="font-medium text-foreground">{email}</span>.
                  Click it to activate your account, then come back to sign in.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleResendConfirmation}
                  disabled={resending}
                >
                  {resending ? "Resending..." : "Resend confirmation email"}
                </Button>
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => setMode('signin')}
                    className="text-sm text-espresso font-medium hover:underline"
                  >
                    Go to sign in
                  </button>
                  <p className="text-sm text-muted-foreground">
                    Wrong email?{" "}
                    <button
                      type="button"
                      onClick={() => setMode('signup')}
                      className="text-espresso font-medium hover:underline"
                    >
                      Go back
                    </button>
                  </p>
                </div>
              </div>
            ) : (
            <>
            <div className="text-center mb-6">
              <h1 className="font-display text-2xl font-bold text-foreground">
                {mode === 'signup' ? "Join the Community" 
                  : mode === 'forgot-password' ? "Reset Password" 
                  : mode === 'reset-password' ? "Set New Password"
                  : "Welcome Back"}
              </h1>
              <p className="text-muted-foreground mt-1">
                {mode === 'signup' 
                  ? "Join the community and stay grounded wherever you work" 
                  : mode === 'forgot-password'
                  ? "Enter your email and we'll send you a reset link"
                  : mode === 'reset-password'
                  ? "Enter your new password below"
                  : "Get grounded. Get focused. Get to work."}
              </p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'signup' && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="firstName" className="text-foreground">First Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="firstName"
                          type="text"
                          placeholder="First"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          className="pl-10 bg-background border-border"
                        />
                      </div>
                      {errors.firstName && <p className="text-sm text-destructive">{errors.firstName}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName" className="text-foreground">Last Name</Label>
                      <Input
                        id="lastName"
                        type="text"
                        placeholder="Last"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="bg-background border-border"
                      />
                      {errors.lastName && <p className="text-sm text-destructive">{errors.lastName}</p>}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="username" className="text-foreground">Username</Label>
                    <div className="relative">
                      <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="username"
                        type="text"
                        placeholder="your.username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/\s/g, ''))}
                        className="pl-10 pr-10 bg-background border-border"
                        maxLength={30}
                      />
                      {username.length >= 3 && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {isCheckingUsername ? (
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                          ) : usernameAvailable === true ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : usernameAvailable === false ? (
                            <X className="h-4 w-4 text-destructive" />
                          ) : null}
                        </div>
                      )}
                    </div>
                    {errors.username && <p className="text-sm text-destructive">{errors.username}</p>}
                    {!errors.username && username.length >= 3 && !isCheckingUsername && usernameAvailable === false && (
                      <p className="text-sm text-destructive">This username is already taken</p>
                    )}
                    {!errors.username && username.length >= 3 && !isCheckingUsername && usernameAvailable === true && (
                      <p className="text-sm text-green-600">Username is available</p>
                    )}
                  </div>
                </>
              )}

              {/* Email field - hide for reset-password mode */}
              {mode !== 'reset-password' && (
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-foreground">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 bg-background border-border"
                    />
                  </div>
                  {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                </div>
              )}

              {/* Password field - show for signin, signup, and reset-password */}
              {mode !== 'forgot-password' && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-foreground">
                      {mode === 'reset-password' ? 'New Password' : 'Password'}
                    </Label>
                    {mode === 'signin' && (
                      <button
                        type="button"
                        onClick={() => setMode('forgot-password')}
                        className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                      >
                        Forgot password?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !loading && mode !== 'reset-password') {
                          e.preventDefault();
                          handleSubmit(e as unknown as React.FormEvent);
                        }
                      }}
                      className="pl-10 pr-10 bg-background border-border"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
                  {(mode === 'signup' || mode === 'reset-password') && (
                    <>
                      {!errors.password && (
                        <p className="text-xs text-muted-foreground">{PASSWORD_REQUIREMENTS_TEXT}</p>
                      )}
                      <PasswordStrengthIndicator password={password} />
                    </>
                  )}
                </div>
              )}

              {/* Confirm Password field - only for reset-password mode */}
              {mode === 'reset-password' && (
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-foreground">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !loading) {
                          e.preventDefault();
                          handleSubmit(e as unknown as React.FormEvent);
                        }
                      }}
                      className="pl-10 pr-10 bg-background border-border"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {errors.confirmPassword && <p className="text-sm text-destructive">{errors.confirmPassword}</p>}
                </div>
              )}

              <Button type="submit" className="w-full btn-cozy text-primary-foreground" disabled={loading}>
                {loading 
                  ? "Please wait..." 
                  : mode === 'signup' 
                  ? "Create Account" 
                  : mode === 'forgot-password'
                  ? "Send Reset Link"
                  : mode === 'reset-password'
                  ? "Update Password"
                  : "Sign In"}
              </Button>
            </form>

            {mode !== 'forgot-password' && mode !== 'reset-password' && (
              <>
                {/* Divider */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
                  </div>
                </div>

                {/* Google Sign In Button */}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                >
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  Continue with Google
                </Button>

                {/* Apple Sign In Button */}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleAppleSignIn}
                  disabled={loading}
                >
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                  {mode === 'signup' ? 'Sign up with Apple' : 'Sign in with Apple'}
                </Button>
              </>
            )}

            <div className="mt-6 text-center space-y-2">
              {mode === 'forgot-password' || mode === 'reset-password' ? (
                <p className="text-sm text-muted-foreground">
                  {mode === 'reset-password' ? "Changed your mind?" : "Remember your password?"}{" "}
                  <button
                    type="button"
                    onClick={() => setMode('signin')}
                    className="text-espresso font-medium hover:underline"
                  >
                    Sign in
                  </button>
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {mode === 'signup' ? "Already have an account?" : "Don't have an account?"}{" "}
                  <button
                    type="button"
                    onClick={() => setMode(mode === 'signup' ? 'signin' : 'signup')}
                    className="text-espresso font-medium hover:underline"
                  >
                    {mode === 'signup' ? "Sign in" : "Sign up"}
                  </button>
                </p>
              )}
            </div>
            </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
